import "./Payment.css"
import Currency, {formatCurrency} from "../../../store/currency/Currency";
import {PaymentRecord} from "../PaymentList"
import Text from "../../../../component/text/Text";
import {IconChevronDown, IconGift} from "@tabler/icons-react";
import {useEffect, useState} from "react";
import Button from "../../../../component/button/Button";
import fetchWithRetry from "../../../../util/request";
import {Link} from "react-router-dom";
import i18n from "../../../../i18n";
import {useTranslation} from "react-i18next";

function formatDate(time: number) {
    const date = new Date(time * 1000);

    return date.toLocaleDateString(i18n.language);
}

function formatItemPreview(items: string[]) {
    if (items.length <= 1) return items.join(", ");

    const visible = items.slice(0, 2);

    if (visible.length < items.length) {
        const more = items.length - visible.length;
        return `${visible.join(", ")} ${i18n.t("payments.andMore", {count: more, items: i18n.t("payments.item", {count: more})})}`;
    }

    return new Intl.ListFormat(i18n.language, {style: "long", type: "conjunction"}).format(visible);
}

function formatState(payment: PaymentRecord): string {
    switch (payment.state) {
        case "COMPLETED":
            return i18n.t("payments.states.completed");
        case "PENDING":
            return i18n.t("payments.states.pending");
        case "CANCELED":
            return i18n.t("payments.states.canceled");
        case "REFUNDED":
            return i18n.t("payments.states.refunded");
    }
}

export default function PaymentComponent(props: {
    payment: PaymentRecord
    currencies: { [key: string]: Currency }
    token: string
}) {
    const {payment, currencies, token} = props;
    const {t} = useTranslation();
    const currency = currencies[payment.currency];

    const [expanded, setExpaned] = useState(false);
    const [paymentLink, setPaymentLink] = useState<string | null>(null);

    useEffect(() => {
        if (payment.state !== "PENDING") return;
        return fetchWithRetry(res => {
            if (res.status === 401) return true;
            if (!res.ok) return false;

            return res.text().then(body => {
                if (body.length > 0)
                    setPaymentLink(body);

                return true;
            })
        }, process.env.REACT_APP_API_BASE + "/store/pay/" + payment.uuid, {
            headers: {
                Authorization: token,
                "Target-Hostname": window.location.origin
            }
        })
    }, [setPaymentLink, token, payment])

    if (!currency) return <Text>{t("payments.invalidCurrency")}</Text>

    currency.multiplied = payment.currency_multiplied;

    return <div className={`payment ${payment.state.toLowerCase()}${expanded ? " expanded" : ""}`}>
        <div className={"fields"} onClick={(e) => {
            let checkEle: HTMLElement = e.target as HTMLElement;

            while (checkEle.parentElement) {
                if (checkEle.classList.contains("btn")) return;
                checkEle = checkEle.parentElement;
            }

            setExpaned((!expanded));
        }}>
            <p className={"date"}>{formatDate(payment.date)}</p>
            <p className={"item-preview"}>{formatItemPreview(payment.items.map(v => v.item))}</p>
            {paymentLink !== null ?
                <Link to={paymentLink}
                      target={"_blank"}><Button>{t("payments.pay", {price: formatCurrency(currency, payment.total)})}</Button></Link> :
                <p className={"total"}>{formatCurrency(currency, payment.total)}</p>
            }
            <IconChevronDown className={"expand-icon"}/>
        </div>
        {!expanded ? null : <div className={"content"}>
            <p className={"state"}>{formatState(payment)}</p>
            <div className={"items"}>
                {payment.items.map((item, i) =>
                    <div key={i} className={"item"}>
                        <p className={"name"}>{item.item}
                            {item.gift ?
                                <span className={"gift-tag"}>
                                <IconGift/>
                                <span className={"user"}>{item.gift_name ?? t("payments.unknownGiftUser")}</span>
                            </span>
                                : null}</p>
                        <p className={"price"}>{formatCurrency(currency, item.price)}</p>
                    </div>
                )}
            </div>
        </div>}
    </div>
}